import { PointFeature } from "supercluster"
import { SIDEBAR_WIDTH } from "../config"
import { ClusterEntity } from "../types/cluster"
import { TripEntity } from "../types/trip"
import { ZoneEntity, ZoneType } from "../types/zone"
import { AlertEntity, AlertBikeEntity, AlertZoneEntity } from "../types/alert"
import { MapBikeEntity } from "../hooks/queries/useGetMapBikesQuery"
import { getBikeStateColor } from "./calcMarket"

export type Coord = [number, number]
export type Polygon = Coord[]
export type Bbox = [Coord, Coord]
export type MultiPolygon = Polygon[]
export type MultiPolygonData = [MultiPolygon]

export type ZoneProperties = {
  zoneType: ZoneType
}

export const BBOX_PADDING = 0.004

const min = (list: number[]) => Math.min(...list)
const max = (list: number[]) => Math.max(...list)

export type Bounds = [{ lat: number; lng: number }, { lat: number; lng: number }]
export const getBounds = (coords: Coord[]): Bounds => {
  const lats: number[] = []
  const lngs: number[] = []

  coords.forEach((coord) => {
    lats.push(coord[1])
    lngs.push(coord[0])
  })
  const latMin = min(lats)
  const lngMin = min(lngs)
  const latMax = max(lats)
  const lngMax = max(lngs)

  return [
    { lat: latMin, lng: lngMin },
    { lat: latMax, lng: lngMax },
  ]
}

export const getMultiClusterBounds = (clusters: ClusterEntity[]) => {
  const coords = clusters.map((c) => c.centroid.coordinates)
  return getBounds(coords)
}

export const getZoneBounds = (zones: ZoneEntity) => {
  const allCoords = zones.polygon.coordinates.flat(2)
  return getBounds(allCoords)
}
export const getMultiZoneBounds = (zones: ZoneEntity[]) => {
  const coords = zones.map((z) => z.polygon.coordinates)
  const allCoords = coords.flat(3)
  return getBounds(allCoords)
}

export const getZonesGeojson = (zones: ZoneEntity[], properties?: ZoneProperties) => ({
  type: "FeatureCollection",
  features: zones
    .filter((zone) => zone.polygon.coordinates.length > 0)
    .map((zone) => ({
      uuid: zone.uuid,
      type: "Feature",
      geometry: { type: "Polygon", coordinates: zone.polygon.coordinates[0] },
      ...(properties && { properties }),
    })),
})

export const getZonesGeojsonWithAllProps = (zones: ZoneEntity[], properties?: ZoneProperties) => ({
  type: "FeatureCollection",
  features: zones
    .filter((zone) => zone.polygon.coordinates.length > 0)
    .map((zone) => ({
      uuid: zone.uuid,
      type: "Feature",
      geometry: { type: "Polygon", coordinates: zone.polygon.coordinates[0] },
      properties: {
        ...(properties && { ...properties }),
        ...zone,
      },
    })),
})

export const getTripsGeojson = (trips: TripEntity[]) => ({
  type: "FeatureCollection",
  features: trips
    .filter((t) => t.route)
    .map((trip) => ({
      type: "Feature",
      geometry: { type: "LineString", coordinates: trip.route!.coordinates },
    })),
})

export const getTripGapsGeojson = (coords: Coord[]) => ({
  type: "FeatureCollection",
  features: coords.map((coordinates) => ({
    type: "Feature",
    geometry: { type: "LineString", coordinates },
  })),
})

export const getBboxQueryParams = (bbox: Bbox) => {
  return "&bbox=" + bbox.flat(2).join("%2C")
}

export const createBboxFromBounds = (bounds: Bounds): Bbox => {
  return [
    [bounds[0].lng - BBOX_PADDING, bounds[0].lat - BBOX_PADDING],
    [bounds[1].lng + BBOX_PADDING, bounds[1].lat + BBOX_PADDING],
  ]
}

export const createBbox = (coords: Coord[]): Bbox => {
  return createBboxFromBounds(getBounds(coords))
}

export const ALERT_MAP_PADDING = {
  left: SIDEBAR_WIDTH,
  top: 50,
  right: 50,
  bottom: 50,
}

export const ZONES_MAP_PADDING = {
  left: SIDEBAR_WIDTH + 50,
  top: 50,
  right: 50,
  bottom: 50,
}

export const checkValidCoordinatesArray = (coordinates: any): boolean => {
  return !!coordinates && coordinates?.length > 0 && coordinates[0] && coordinates[1]
}

export const getBikeFeatures = (
  bicycle: MapBikeEntity,
): PointFeature<{ bicycle: MapBikeEntity; color: string }> => ({
  type: "Feature",
  geometry: { type: "Point", coordinates: [bicycle.longitude, bicycle.latitude] },
  properties: { bicycle, color: getBikeStateColor(bicycle.state) },
})

export const getAlertFeatures = (
  alert: [
    string,
    {
      alerts: AlertEntity[]
      bicycle?: AlertBikeEntity
      zone?: AlertZoneEntity
    },
  ],
): PointFeature<{
  bicycle?: AlertBikeEntity
  zone?: AlertZoneEntity
  alerts: AlertEntity[]
  state?: string
}> => {
  if (alert[1].bicycle?.bicycle_coordinates) {
    return {
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [
          // This function should always be called with current bycicle coordinates
          // eslint-disable-next-line
          alert[1].bicycle?.bicycle_coordinates[0]!,
          // eslint-disable-next-line
          alert[1].bicycle?.bicycle_coordinates[1]!,
        ],
      },
      properties: {
        bicycle: alert[1].bicycle,
        zone: alert[1].zone,
        alerts: alert[1].alerts,
        state: alert[1].bicycle?.bicycle_state,
      },
    }
  } else {
    return {
      type: "Feature",
      geometry: {
        type: "Point",
        // This function should always be called with location coordinates
        // eslint-disable-next-line
        coordinates: [alert[1].zone?.location!.coordinates[0]!, alert[1].zone?.location!.coordinates[1]!],
      },
      properties: {
        bicycle: undefined,
        zone: alert[1].zone,
        alerts: alert[1].alerts,
        state: undefined,
      },
    }
  }
}
